import { useForm } from '@tanstack/react-form'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useLoginMutation, useWhoamiMutation } from '@unsw/api/mutations/auth'
import { useAuthStore } from '@unsw/store/auth'
import { Alert, AlertDescription } from '@unsw/ui/components/ui/alert'
import { Button } from '@unsw/ui/components/ui/button'
import { Card, CardContent } from '@unsw/ui/components/ui/card'
import { Input } from '@unsw/ui/components/ui/input'
import { KeyRound, Loader2, User } from 'lucide-react'
import { useEffect } from 'react'
import backgroundShape from '../assets/images/background-shape.svg'
import logo from '../assets/images/mentorai_yellow.png'

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: search.redirect as string | undefined,
  }),
})

function LoginPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/login' })
  const redirect = search.redirect || '/dashboard'
  const { setTokens, setUser, tokens } = useAuthStore()

  const loginMutation = useLoginMutation()
  const whoamiMutation = useWhoamiMutation()

  const form = useForm({
    defaultValues: {
      username: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      try {
        const tokenResponse = await loginMutation.mutateAsync({
          username: value.username,
          password: value.password,
        })
        setTokens(tokenResponse)

        const user = await whoamiMutation.mutateAsync()
        setUser(user)

        navigate({ to: redirect })
      } catch (error) {
        console.error('Login failed:', error)
      }
    },
  })

  // Redirect if already logged in
  useEffect(() => {
    if (tokens?.access_token) {
      navigate({ to: redirect })
    }
  }, [tokens, navigate, redirect])

  const isLoading = loginMutation.isPending || whoamiMutation.isPending

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-unsw-yellow p-4 overflow-hidden">
      {/* Background Shape */}
      <img
        src={backgroundShape}
        alt=""
        aria-hidden="true"
        className="absolute -top-48 -right-36 w-[950px] h-[950px] pointer-events-none"
      />

      <Card className="relative z-10 w-full max-w-[400px] border-0 shadow-xl bg-white">
        <CardContent className="pt-10 pb-8 px-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={logo} alt="UNSW Mentor AI" className="w-[190px]" />
          </div>

          {/* Title */}
          <h1 className="text-xl font-bold text-center mb-2">Sign in</h1>
          <p className="text-muted-foreground text-center mb-6">to continue to Mentor AI</p>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="space-y-4"
          >
            {/* Error Alert */}
            {loginMutation.isError && (
              <Alert variant="destructive">
                <AlertDescription>Please verify your username and password</AlertDescription>
              </Alert>
            )}

            {/* Username Input */}
            <form.Field
              name="username"
              validators={{
                onChange: ({ value }) => (!value ? 'Username is required' : undefined),
              }}
            >
              {(field) => (
                <div>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="username"
                      type="text"
                      placeholder="Username"
                      className="pl-10 h-12"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={field.state.meta.errors.filter(Boolean).length > 0}
                    />
                  </div>
                  {field.state.meta.errors.filter(Boolean).length > 0 && (
                    <p className="text-sm text-destructive mt-1">
                      {field.state.meta.errors.filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Password Input */}
            <form.Field
              name="password"
              validators={{
                onChange: ({ value }) => (!value ? 'Password is required' : undefined),
              }}
            >
              {(field) => (
                <div>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Password"
                      className="pl-10 h-12"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={field.state.meta.errors.filter(Boolean).length > 0}
                    />
                  </div>
                  {field.state.meta.errors.filter(Boolean).length > 0 && (
                    <p className="text-sm text-destructive mt-1">
                      {field.state.meta.errors.filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Forgot Password */}
            <p className="text-sm text-muted-foreground">
              Forgot your password?{' '}
              <a
                href="mailto:support@ispeak.to?subject=Forgot Password&body=I forgot my password, please assist."
                className="text-secondary hover:underline underline-offset-4"
              >
                Contact tech support
              </a>
            </p>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-12 font-mono font-normal tracking-wide"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
